import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SequenceDetail as ISequenceDetail, CreatorSequence, Sequence } from '@/types/sequence';
import { getSequenceDetails, getCreatorSequences } from '@/api/sequences';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useRatingStats } from '@/hooks/useRatingStats';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Heart,
  Download,
  Clock,
  Star,
  Share,
  ArrowLeft,
  Settings
} from 'lucide-react';
import SequenceCreatorCard from '@/components/sequences/SequenceCreatorCard';
import AuthModal from '@/components/auth/AuthModal';
import DownloadAuthModal from '@/components/download/DownloadAuthModal';
import { ClaimButton } from '@/components/sequences/ownership';
import SequenceProps from '@/components/sequences/SequenceProps';
import SequenceBreadcrumbs from '@/components/sequences/SequenceBreadcrumbs';
import RatingSummary from '@/components/reviews/RatingSummary';
import RecentReviewsHighlight from '@/components/reviews/RecentReviewsHighlight';
import { 
  SchemaMarkup, 
  createProductSchema, 
  createCreativeWorkSchema, 
  createBreadcrumbSchema 
} from '@/components/seo/SchemaMarkup';
import ReviewSection from '@/components/reviews/ReviewSection';
import { useSequencePurchase } from '@/hooks/useSequencePurchase';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { AddedToDisplayModal } from '@/components/download/AddedToDisplayModal';
import { GoFundMeDonationModal } from '@/components/download/GoFundMeDonationModal';
import { ConfirmationPage } from '@/components/download/ConfirmationPage';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import SequenceMetadata from '@/components/sequences/SequenceMetadata';
import { formatHolidayName, formatSequencePrice } from '@/utils/formatters';
import SmartUpvoteButton from '@/components/shared/SmartUpvoteButton';
import LayoutPhotosGallery from '@/components/sequences/LayoutPhotosGallery';
import { layoutPhotoService } from '@/services/layoutPhotoService';

const SequenceDetailPage: React.FC = () => {
  console.log('SequenceDetail component rendering');
  const { id } = useParams<{ id: string }>();
  const [sequence, setSequence] = useState<ISequenceDetail | null>(null);
  const [creatorSequences, setCreatorSequences] = useState<CreatorSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalAction, setAuthModalAction] = useState<'favorite' | 'download'>('download');
  const [layoutPhotos, setLayoutPhotos] = useState<any[]>([]);
  const { user, isAuthenticated } = useAuth();
  const { stats: ratingStats, isLoading: isLoadingRating } = useRatingStats('sequence', id || '');
  const navigate = useNavigate();
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavoritesStore();

  // Use the sequence purchase hook for download functionality
  const {
    isOwner,
    isPurchased,
    canDownload,
    handleDownload: handleSequenceDownload,
    isDownloading,
    checkingOwnership,
  } = useSequencePurchase(id);

  // Download flow store for modal management
  const downloadStore = useDownloadFlowStore();
  const {
    modalState,
    currentSession,
    hideAddedToDisplayModal,
    hideDonationModal,
    hideConfirmationPage,
    endDownloadSession,
    showAddedToDisplayModal,
    getNextModalInFlow,
    showConfirmationPage,
  } = downloadStore;

  // Admin check
  const isAdmin = user?.email === 'joey@towb.in';

  useEffect(() => {
    console.log('Loading sequence with ID:', id);
    const loadData = async () => {
      try {
        if (!id) throw new Error('No sequence ID provided');
        
        // Load sequence details
        const sequenceData = await getSequenceDetails(id);
        setSequence(sequenceData);

        // Load layout photos for this sequence
        try {
          const photos = await layoutPhotoService.getLayoutPhotos(id);
          setLayoutPhotos(photos);
        } catch (photoError) {
          console.error('Error loading layout photos:', photoError);
          // Continue without photos - not critical
        }

        // Load creator's other sequences if we have a valid creator ID
        if (sequenceData.creator && sequenceData.creator.id && 
            sequenceData.creator.id !== 'unknown' && 
            sequenceData.creator.id !== 'null' && 
            sequenceData.creator.id.trim() !== '') {
          const creatorData = await getCreatorSequences(sequenceData.creator.id);
          // Filter out the current sequence and limit to 4 items
          const otherSequences = creatorData.sequences
            .filter(seq => seq.id !== id)
            .slice(0, 4);
          setCreatorSequences(otherSequences);
        } else {
          // Set empty array if no valid creator ID
          setCreatorSequences([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load sequence');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  console.log('Current state:', { loading, error, sequence });

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        <div className="container mx-auto px-4 py-4 pt-24">
          <Skeleton className="w-full h-[400px] mb-4" />
          <div className="mt-2">
            <Skeleton className="w-full h-[60px] mb-2" />
            <Skeleton className="w-full h-[30px] mb-2" />
            <div className="mt-2 flex gap-4">
              <div className="flex-2">
                <Skeleton className="w-full h-[200px]" />
              </div>
              <div className="flex-1">
                <Skeleton className="w-full h-[200px]" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !sequence) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        <div className="container mx-auto px-4 py-4 pt-24">
          <Alert className="mb-4">
            <AlertDescription>
              {error || 'Sequence not found'}
            </AlertDescription>
          </Alert>
          <Button asChild>
            <Link to="/sequences">
              Back to Sequences
            </Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  console.log('Rendering sequence details:', sequence);

  const handleFavorite = () => {
    if (!isAuthenticated) {
      setAuthModalAction('favorite');
      setShowAuthModal(true);
      return;
    }
    
    if (id && sequence) {
      if (isFavorite(id)) {
        removeFromFavorites(id);
        toast.success("Removed from favorites", {
          description: "Sequence removed from your favorites"
        });
      } else {
        addToFavorites({
          id: sequence.id,
          title: sequence.title || 'Untitled Sequence',
          imageUrl: sequence.imageUrl,
          type: 'sequence'
        });
        toast.success("Added to favorites", {
          description: "Sequence added to your favorites"
        });
      }
    }
  };

  const openAuthModal = () => {
    setAuthModalAction('favorite');
    setShowAuthModal(true);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    
    if (authModalAction === 'favorite' && id && sequence) {
      addToFavorites({
        id: sequence.id,
        title: sequence.title || 'Untitled Sequence',
        imageUrl: sequence.imageUrl,
        type: 'sequence'
      });
      toast.success("Added to favorites", {
        description: "Sequence added to your favorites"
      });
    } else if (authModalAction === 'download' && sequence) {
      // Trigger download after successful authentication
      setTimeout(() => {
        const sequenceForDownload: Sequence = {
          id: sequence.id,
          title: sequence.title,
          displayName: sequence.title,
          price: sequence.price,
          isFree: sequence.isFree,
          software: sequence.software,
          holiday: sequence.holiday,
          imageUrl: sequence.imageUrl,
          song: sequence.song,
          createdAt: sequence.createdAt,
          downloadCount: sequence.downloadCount,
          averageRating: sequence.averageRating,
          ratingCount: sequence.ratingCount,
          featured: sequence.featured,
          file_url: sequence.file_url,
          creator: sequence.creator,
        };
        handleSequenceDownload(sequenceForDownload);
      }, 100);
    }
  };

  const handleDownload = () => {
    console.log('Download button clicked:', { 
      isAuthenticated, 
      price: sequence?.price, 
      isFree: sequence?.isFree,
      isOwner, 
      isPurchased, 
      canDownload 
    });
    
    if (!isAuthenticated) {
      setAuthModalAction('download');
      setShowAuthModal(true);
      return;
    }
    
    if (sequence) {
      // Convert SequenceDetail to Sequence format expected by the hook
      const sequenceForDownload: Sequence = {
        id: sequence.id,
        title: sequence.title,
        displayName: sequence.title,
        price: sequence.price,
        isFree: sequence.isFree,
        software: sequence.software,
        holiday: sequence.holiday,
        imageUrl: sequence.imageUrl,
        song: sequence.song,
        createdAt: sequence.createdAt,
        downloadCount: sequence.downloadCount,
        averageRating: sequence.averageRating,
        ratingCount: sequence.ratingCount,
        featured: sequence.featured,
        file_url: sequence.file_url,
        creator: sequence.creator,
      };
      
      console.log('Calling handleSequenceDownload with:', sequenceForDownload);
      // The handleSequenceDownload already handles modal flow and progress
      handleSequenceDownload(sequenceForDownload);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: sequence.title || 'Light Sequence',
          text: `Check out this ${sequence.holiday || 'holiday'} light sequence: ${sequence.title || 'Untitled Sequence'}`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Handle admin edit button click
  const handleAdminEdit = () => {
    if (!id) return;
    // Open admin panel in new tab with sequence pre-selected
    const adminUrl = `/admin?sequence=${encodeURIComponent(id)}`;
    window.open(adminUrl, '_blank');
  };

  // Generate schema markup for sequence
  const generateSchemaMarkup = () => {
    if (!sequence) return null;

    const currentUrl = window.location.href;

    // Product Schema for commercial aspects
    const productSchema = createProductSchema(
      sequence.title,
      sequence.description,
      sequence.imageUrl || sequence.videoUrl,
      {
        price: sequence.price.toString(),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: currentUrl
      },
      'Light Display Sequence',
      `${sequence.holiday || 'Holiday'} ${sequence.software || 'Light Display'}`,
      {
        ratingValue: sequence.averageRating,
        reviewCount: sequence.ratingCount
      }
    );

    // Creative Work Schema for artistic aspects
    const creativeWorkSchema = createCreativeWorkSchema(
      sequence.title,
      sequence.description,
      {
        name: sequence.creator?.username || 'Unknown Creator',
        url: sequence.creator?.id && sequence.creator.id !== 'unknown' ? 
          `${window.location.origin}/creator/${sequence.creator.id}` : 
          window.location.origin
      },
      sequence.createdAt,
      sequence.song?.genre || sequence.holiday || 'Holiday',
      [
        sequence.holiday || 'Holiday',
        sequence.software || 'Light Display',
        'light sequence',
        'holiday lights',
        'synchronized lights',
        sequence.song?.title || sequence.title,
        sequence.song?.artist || 'Unknown Artist'
      ],
      currentUrl,
      sequence.imageUrl,
      'Commercial use allowed'
    );

    // Breadcrumb Schema - with null checks
    const breadcrumbItems = [
      { name: 'Home', url: window.location.origin },
      { name: 'Sequences', url: `${window.location.origin}/sequences` }
    ];
    
    // Only add software breadcrumb if software exists
    if (sequence.software) {
      breadcrumbItems.push({
        name: sequence.software,
        url: `${window.location.origin}/sequences/${sequence.software.toLowerCase()}`
      });
    }
    
    // Only add holiday breadcrumb if both software and holiday exist
    if (sequence.software && sequence.holiday) {
      breadcrumbItems.push({
        name: sequence.holiday,
        url: `${window.location.origin}/sequences/${sequence.software.toLowerCase()}/${sequence.holiday.toLowerCase()}`
      });
    }
    
    // Always add the final sequence title breadcrumb
    breadcrumbItems.push({
      name: sequence.title || 'Untitled Sequence',
      url: currentUrl
    });
    
    const breadcrumbSchema = createBreadcrumbSchema(breadcrumbItems);

    return { productSchema, creativeWorkSchema, breadcrumbSchema };
  };

  const schemas = generateSchemaMarkup();

  // Generate SEO content with null safety
  const sequenceTitle = sequence?.title || 'Untitled Sequence';
  const softwareName = sequence?.software || 'Unknown Software';
  const holidayName = sequence?.holiday ? formatHolidayName(sequence.holiday) : '';
  const artistName = sequence?.song?.artist || 'Unknown Artist';
  const songTitle = sequence?.song?.title || sequenceTitle;
  
  const pageTitle = `${sequenceTitle} - ${softwareName} ${holidayName} Light Sequence | LightsHunt`;
  const metaDescription = `Download ${sequenceTitle} by ${artistName}, a ${holidayName} light sequence for ${softwareName}. ${sequence?.description || ''} Price: $${sequence?.price || 0}`;
  const keywords = `${sequenceTitle}, ${artistName}, ${holidayName} lights, ${softwareName}, light sequence, holiday display, synchronized lights, ${songTitle}`;

  return (
    <>
      {sequence && (
        <>
          <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={keywords} />
            
            {/* Open Graph tags */}
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:type" content="product" />
            <meta property="og:url" content={window.location.href} />
            {sequence.imageUrl && <meta property="og:image" content={sequence.imageUrl} />}
            <meta property="product:price:amount" content={sequence.price.toString()} />
            <meta property="product:price:currency" content="USD" />
            
            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {sequence.imageUrl && <meta name="twitter:image" content={sequence.imageUrl} />}
            
            {/* Music/Audio specific meta tags */}
            <meta property="music:song" content={sequence.song?.title || sequence.title} />
            <meta property="music:musician" content={sequence.song?.artist || 'Unknown Artist'} />
            <meta property="music:album" content={sequence.song?.album || ''} />
            
            {/* Canonical URL */}
            <link rel="canonical" href={window.location.href} />
          </Helmet>

          {/* Schema Markup */}
          {schemas && (
            <>
              <SchemaMarkup schema={schemas.productSchema} />
              <SchemaMarkup schema={schemas.creativeWorkSchema} />
              <SchemaMarkup schema={schemas.breadcrumbSchema} />
            </>
          )}
        </>
      )}

      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        <div className="container mx-auto px-4 py-4 pt-24">
          {/* Breadcrumbs */}
          <SequenceBreadcrumbs
            title={sequence.title}
            software={sequence.software}
            holiday={sequence.holiday}
            className="mb-4"
          />

          {/* Title Section with Heart */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-foreground">
                {sequence.title}
              </h1>
            </div>
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFavorite}
                className="p-2"
              >
                <Heart className={`w-6 h-6 ${isFavorite(sequence.id) ? 'fill-current text-red-500' : 'text-gray-500'}`} />
              </Button>
            </div>
          </div>

          {/* Genre Chips */}
          {(Array.isArray(sequence.genres) && sequence.genres.length > 0) && (
            <div className="mb-6 flex flex-wrap gap-2">
              {sequence.genres.map((g) => {
                const isPrimary = sequence.primary_genre && g === sequence.primary_genre;
                const isSecondary = sequence.secondary_genre && g === sequence.secondary_genre;
                return (
                  <Link key={g} to={`/sequences?genres=${encodeURIComponent(g)}`} className="no-underline">
                    <Badge variant={isPrimary ? 'default' : 'outline'} className={isSecondary && !isPrimary ? 'border-blue-400 text-blue-600' : ''}>
                      {g}
                      {isPrimary ? ' (Primary)' : isSecondary ? ' (Secondary)' : ''}
                    </Badge>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Main Content */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-8">
            {/* Left Column */}
            <div className="flex-1 md:flex-2 min-w-0">
              
              {/* Video Player / Featured Image */}
              <div className="mb-6">
                <VideoPlayer
                  videoUrl={sequence.videoUrl || ''}
                  title={sequence.title}
                  imageUrl={sequence.imageUrl}
                  aspectRatio="video"
                  autoplay={true}
                />
              </div>

              {/* Layout Photos Gallery - displayed below video */}
              {layoutPhotos.length > 0 && (
                <div className="mb-6">
                  <LayoutPhotosGallery
                    photos={layoutPhotos}
                    sequenceTitle={sequence.title}
                  />
                </div>
              )}

              {/* Mobile: full Download/Actions card after video and above description */}
              <div className="md:hidden mb-6">
                <Card className="mb-6">
                  <CardContent>
                    <h5 className="text-2xl font-bold text-foreground mb-4">
                      {formatSequencePrice(sequence.price ?? 0)}
                    </h5>
                    <Button
                      className="w-full mb-3"
                      onClick={handleDownload}
                      disabled={isDownloading || (checkingOwnership && sequence.price > 0)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {checkingOwnership
                        ? 'Checking access...'
                        : isDownloading
                          ? 'Downloading...'
                          : sequence.price === 0
                            ? 'Download Free'
                            : isPurchased
                              ? 'Re-download'
                              : `Purchase $${sequence.price}`}
                    </Button>
                    <SmartUpvoteButton
                      target="sequence"
                      id={sequence.id}
                      holidayType={sequence.holiday_type}
                      className="w-full"
                      onRequireAuth={openAuthModal}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleShare}
                    >
                      <Share className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        className="w-full mt-3 border-orange-500 text-orange-500 hover:bg-orange-50"
                        onClick={handleAdminEdit}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Edit in Admin
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Mobile: Sequence Details and Creator sections after download */}
              <div className="md:hidden mb-6 space-y-6">
                {/* Sequence Metadata */}
                <SequenceMetadata sequence={sequence} />

                {/* Creator Info */}
                <SequenceCreatorCard
                  avatarUrl={sequence.creator?.avatarUrl || ''}
                  creatorName={sequence.creator?.username || 'Unknown Creator'}
                  creatorId={sequence.creator?.id || ''}
                  profileUsername={sequence.creator?.username || undefined}
                  rating={4.5}
                  sequenceCount={0}
                />

                {/* Ownership Claim Section */}
                <ClaimButton
                  sequenceId={sequence.id}
                  creatorId={sequence.creator?.id || ''}
                  claimStatus={sequence.ownership?.claimStatus || 'unclaimed'}
                />
              </div>

              {/* Sequence Info */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {ratingStats && ratingStats.rating_count > 0 ? (
                        <>
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-5 h-5 ${
                                  star <= Math.round(ratingStats.average_rating)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="ml-2 text-sm text-muted-foreground">
                            ({ratingStats.average_rating.toFixed(1)}) • {ratingStats.rating_count} {ratingStats.rating_count === 1 ? 'review' : 'reviews'}
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className="w-5 h-5 text-gray-300" />
                            ))}
                          </div>
                          <span className="ml-2 text-sm text-muted-foreground">
                            No reviews yet
                          </span>
                        </>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleFavorite}
                      className="flex items-center text-sm hover:bg-red-50 hover:text-red-600"
                    >
                      <Heart className={`w-4 h-4 mr-1 ${isFavorite(sequence.id) ? 'fill-current text-red-500' : 'text-gray-500'}`} />
                      Add to Favorites
                    </Button>
                  </div>

                  <p className="text-foreground mb-4">
                    {sequence.description}
                  </p>

                  {/* More by Creator - Horizontal Carousel */}
                  {creatorSequences.length > 0 && (
                    <div className="mb-6">
                      <h6 className="text-lg font-semibold text-foreground mb-3">
                        More by {sequence.creator?.username || 'Unknown Creator'}
                      </h6>
                      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-muted-foreground scrollbar-track-transparent">
                        {creatorSequences.map((seq) => {
                          // Get image URL with album cover fallback
                          const getSequenceImageUrl = () => {
                            if (seq.imageUrl && seq.imageUrl.trim() !== '') {
                              return seq.imageUrl;
                            }
                            if (seq.album_cover_url && seq.album_cover_url.trim() !== '') {
                              return seq.album_cover_url;
                            }
                            return seq.imageUrl; // Fallback to original (might be null/empty)
                          };

                          return (
                            <Link 
                              key={seq.id} 
                              to={`/sequence/${seq.id}`}
                              className="block shrink-0 no-underline"
                            >
                              <Card className="w-48 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg overflow-hidden">
                                <div className="h-48 relative">
                                  <img
                                    src={getSequenceImageUrl()}
                                    alt={seq.title}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <CardContent className="p-3">
                                  <h6 className="font-semibold text-sm text-foreground overflow-hidden text-ellipsis whitespace-nowrap mb-1">
                                    {seq.title}
                                  </h6>
                                  <p className="text-sm font-semibold text-primary">
                                    ${(seq.price ?? 0).toFixed(2)}
                                  </p>
                                </CardContent>
                              </Card>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Mobile: Download/Price section shown here above reviews */}
                  <div className="md:hidden mb-6">
                    <Separator className="my-4" />
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-2xl font-bold text-foreground">
                        {formatSequencePrice(sequence.price ?? 0)}
                      </h5>
                    </div>
                    <Button
                      className="w-full mb-3"
                      onClick={handleDownload}
                      disabled={isDownloading || (checkingOwnership && sequence.price > 0)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {checkingOwnership 
                        ? 'Checking access...' 
                        : isDownloading 
                          ? 'Downloading...' 
                          : sequence.price === 0
                            ? 'Download Free'
                            : isPurchased 
                              ? 'Re-download' 
                              : `Purchase $${sequence.price}`
                      }
                    </Button>
                  </div>

                  <Separator className="my-4" />

                  {/* Add Props Section */}
                  <SequenceProps 
                    props={sequence.props || []} 
                    currentHoliday={sequence.holiday || 'Christmas'}
                  />
                  
                  {/* Rating Summary and Recent Reviews - moved here from below */}
                  {ratingStats && ratingStats.rating_count > 0 && (
                    <div className="mt-8">
                      <Separator className="mb-6" />
                      <h5 className="text-xl font-bold text-foreground mb-4">
                        Reviews & Ratings
                      </h5>
                      <div className="space-y-6">
                        <RatingSummary 
                          stats={ratingStats}
                          showDistribution={true}
                          showPercentages={true}
                        />
                        <RecentReviewsHighlight
                          targetType="sequence"
                          targetId={sequence.id}
                          maxReviews={3}
                          showTitle={false}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Reviews Section - moved here from below */}
                  <div className="mt-8">
                    <ReviewSection
                      targetType="sequence"
                      targetId={sequence.id}
                      targetName={sequence.title}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column */}
            <div className="flex-1 md:flex-[0_0_33.333333%] min-w-0">
              {/* Price and Actions */}
              <Card className="mb-6 hidden md:block">
                <CardContent>
                  <h5 className="text-2xl font-bold text-foreground mb-4">
                    {formatSequencePrice(sequence.price ?? 0)}
                  </h5>
                  <Button
                    className="w-full mb-3"
                    onClick={handleDownload}
                    disabled={isDownloading || (checkingOwnership && sequence.price > 0)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {checkingOwnership 
                      ? 'Checking access...' 
                      : isDownloading 
                        ? 'Downloading...' 
                        : sequence.price === 0
                          ? 'Download Free'
                          : isPurchased 
                            ? 'Re-download' 
                            : `Purchase $${sequence.price}`
                    }
                  </Button>
                  <SmartUpvoteButton
                    target="sequence"
                    id={sequence.id}
                    holidayType={sequence.holiday_type}
                    className="w-full"
                    onRequireAuth={openAuthModal}
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleShare}
                  >
                    <Share className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  
                  {/* Admin Edit Button - only visible to super admin */}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      className="w-full mt-3 border-orange-500 text-orange-500 hover:bg-orange-50"
                      onClick={handleAdminEdit}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Edit in Admin
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Sequence Metadata */}
              <SequenceMetadata sequence={sequence} />

              {/* Creator Info */}
              <SequenceCreatorCard
                avatarUrl={sequence.creator?.avatarUrl || ''}
                creatorName={sequence.creator?.username || 'Unknown Creator'}
                creatorId={sequence.creator?.id || ''}
                profileUsername={sequence.creator?.username || undefined}
                rating={4.5}
                sequenceCount={0}
              />

              {/* Ownership Claim Section */}
              <ClaimButton
                sequenceId={sequence.id}
                creatorId={sequence.creator?.id || ''}
                claimStatus={sequence.ownership?.claimStatus || 'unclaimed'}
              />

              {/* Recent Reviews Sidebar */}
              <div className="mt-6 hidden md:block">
                <RecentReviewsHighlight
                  targetType="sequence"
                  targetId={sequence.id}
                  maxReviews={2}
                  showTitle={true}
                />
              </div>
            </div>
          </div>
        </div>
        <Footer />
        
        {authModalAction === 'favorite' ? (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onSuccess={handleAuthSuccess}
            title="Sign in to save favorites"
            description="Sign in to save this sequence to your favorites and access it later."
          />
        ) : (
          <DownloadAuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onSuccess={handleAuthSuccess}
            sequenceId={sequence?.id}
            sequenceTitle={sequence?.title}
            sequenceArtist={sequence?.song?.artist}
            isFree={!!sequence?.isFree}
            price={sequence?.price}
            isOwner={false}
            albumCoverUrl={sequence?.album_cover_url || sequence?.song?.imageUrl}
          />
        )}

        {/* Download Flow Modals */}
        {/* Donation Modal - for charity sequences */}
        {modalState.isDonationModalVisible && (sequence as any)?.charity && (
          <GoFundMeDonationModal
            isOpen={modalState.isDonationModalVisible}
            onClose={hideDonationModal}
            charity={(sequence as any).charity}
            sequenceTitle={sequence.title}
            sequenceArtist={sequence.song?.artist || (sequence as any).artist || 'Unknown Artist'}
            onAfterDismiss={showAddedToDisplayModal}
          />
        )}

        {/* Added to Display Modal - after download completion */}
        {modalState.isAddedToDisplayModalVisible && currentSession && (
          <AddedToDisplayModal
            isOpen={modalState.isAddedToDisplayModalVisible}
            onClose={() => {
              hideAddedToDisplayModal();
              // Automatically trigger confirmation page after this modal
              setTimeout(() => {
                const nextModal = getNextModalInFlow();
                if (nextModal === 'confirmation') {
                  showConfirmationPage();
                }
              }, 500);
            }}
            sequenceId={currentSession.sequenceId}
            sequenceTitle={currentSession.sequenceTitle}
            sequenceArtist={currentSession.sequenceArtist}
            onAfterAction={() => {
              console.log('AddedToDisplayModal closed, flow continuing...');
            }}
          />
        )}

        {/* Confirmation Page - final step showing success and related sequences */}
        {modalState.isConfirmationPageVisible && currentSession && (
          <ConfirmationPage
            onContinue={() => {
              hideConfirmationPage();
              endDownloadSession();
              console.log('Download flow completed');
            }}
          />
        )}

      </div>
    </>
  );
};

export default SequenceDetailPage;
